import { useEffect, useRef, useState } from "react";

const problems = [
  { icon: "ri-hotel-bed-line", text: "Getting out of bed feels harder" },
  { icon: "ri-stairs-line", text: "Stairs require more effort" },
  { icon: "ri-walk-line", text: "Long walks are less enjoyable" },
  { icon: "ri-armchair-line", text: "Your joints feel stiff after sitting" },
];

export default function ProblemSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-16 md:py-24 bg-background-50">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
        <div
          className={`flex flex-col lg:flex-row gap-12 lg:gap-20 transition-all duration-600 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)" }}
        >
          <div className="lg:w-1/2">
            <p className="text-accent-500 text-base font-medium tracking-[0.2em] uppercase mb-3">
              You&apos;re Not Alone
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              Small daily moments are starting to feel different.
            </h2>
            <p className="text-foreground-700 text-xl leading-relaxed">
              For millions of adults over 50, the change is subtle at first. Then
              it becomes part of every morning, every staircase, every walk.
            </p>
          </div>
          <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {problems.map((item, idx) => (
              <div
                key={item.text}
                className={`flex items-center gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70 card-hover-lift transition-all duration-600 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
                }`}
                style={{
                  transitionDelay: `${idx * 80}ms`,
                  transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
                }}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                  <i className={`${item.icon} text-xl`} />
                </div>
                <p className="text-foreground-800 text-lg font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}